import { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface SEOProps {
  title?: string;
  description?: string;
}

const seoContent = {
  fr: {
    default: {
      title: 'Gestion de Vente & IA | Genius Vente - Solution POS Complète avec Imprimante Thermique',
      description: 'Logiciel de gestion de vente complet avec imprimante thermique et système POS propulsé par l\'IA. Automatisation, analyse prédictive et gestion optimisée pour votre commerce.',
      keywords: 'gestion de vente, logiciel gestion vente, IA gestion commerce, logiciel POS, système point de vente, imprimante thermique, ticket caisse, caisse enregistreuse, gestion commerciale, logiciel caisse',
    },
    sales: {
      title: 'Gestion de Vente Intelligente | Système POS & Imprimante Thermique | Genius Vente',
      description: 'Logiciel de gestion de vente avec IA et imprimante thermique. Solution complète pour le point de vente, analyse prédictive et optimisation commerciale.',
      keywords: 'gestion de vente, logiciel caisse, système vente, point de vente, caisse enregistreuse, imprimante ticket, logiciel commercial, IA ventes, prédiction ventes, ticket caisse personnalisé',
    },
    inventory: {
      title: 'Gestion des Stocks & Ventes | Logiciel POS avec Impression Thermique | Genius Vente',
      description: 'Solution complète de gestion de vente et d\'inventaire avec impression thermique. Optimisation des stocks, étiquettes thermiques et analyse prédictive.',
      keywords: 'gestion de vente et stock, logiciel inventaire commercial, système gestion magasin, étiquette thermique, gestion commerciale, imprimante code-barres, logiciel caisse et stock',
    },
    analytics: {
      title: 'Analyses & Tableaux de Bord pour Gestion de Vente | Genius Vente',
      description: 'Tableaux de bord et analyses avancées pour votre gestion de vente. Insights automatiques, détection des tendances et prédictions personnalisées.',
      keywords: 'gestion de vente analyse, tableau de bord vente, statistiques commerciales, analyse prédictive vente, reporting vente, business intelligence commerce, indicateurs performance vente',
    },
  },
  en: {
    default: {
      title: 'Sales Management & AI | Genius Vente - Complete POS Solution with Thermal Printer',
      description: 'Complete sales management software with thermal printer and AI-powered POS system. Smart automation, predictive analytics, and optimized management for your business.',
      keywords: 'sales management, sales management software, AI retail management, POS software, point of sale system, thermal printer, receipt printing, cash register, retail management, checkout software',
    },
    sales: {
      title: 'Smart Sales Management | POS System & Thermal Printer | Genius Vente',
      description: 'Sales management software with AI and thermal printing capabilities. Complete solution for point of sale, predictive analytics and retail optimization.',
      keywords: 'sales management, checkout software, sales system, point of sale, cash register, receipt printer, retail software, AI sales, sales prediction, custom receipt printing',
    },
    inventory: {
      title: 'Inventory & Sales Management | POS Software with Thermal Printing | Genius Vente',
      description: 'Complete sales and inventory management solution with thermal printing. Stock optimization, thermal labels and predictive analytics.',
      keywords: 'sales and inventory management, retail inventory software, store management system, thermal label, retail management, barcode printer, checkout and stock software',
    },
    analytics: {
      title: 'Analytics & Dashboards for Sales Management | Genius Vente',
      description: 'Dashboards and advanced analytics for your sales management. Automated insights, trend detection and personalized predictions.',
      keywords: 'sales management analytics, sales dashboard, retail statistics, predictive sales analysis, sales reporting, retail business intelligence, sales performance metrics',
    },
  },
};

interface PageSEOConfig {
  isIndexable?: boolean;
  priority?: string;
  changefreq?: string;
  isCanonical?: boolean;
  customCanonicalPath?: string;
}

export const SEO: React.FC<SEOProps & PageSEOConfig> = ({ 
  title, 
  description, 
  isIndexable = true, 
  priority = '0.7',
  changefreq = 'monthly',
  isCanonical = true,
  customCanonicalPath
}) => {
  const { language } = useLanguage();
  const location = useLocation();
  const { user } = useAuth();
  
  // Support pour le SSR ou rendu statique: ces valeurs peuvent être utilisées dans un contexte getStaticProps/getServerSideProps
  // dans une implémentation NextJS future

  useEffect(() => {
    // Déterminer la page actuelle de manière plus robuste
    const urlPathSegments = location.pathname.split('/').filter(Boolean);
    const currentPage = urlPathSegments.length > 0 ? urlPathSegments[0] as keyof typeof seoContent.fr : 'default';
    const content = seoContent[language as keyof typeof seoContent][currentPage] || seoContent[language as keyof typeof seoContent].default;
    
    // Détection de pages spécifiques qui ne devraient pas être indexées
    const nonIndexablePages = ['admin', 'test', 'dev', 'preview', 'beta', 'dashboard'];
    const shouldIndex = isIndexable && !nonIndexablePages.some(page => location.pathname.includes(page));
    
    // Mise à jour des méta-tags standards - optimisé pour la performance
    document.title = title || content.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description || content.description);
    // Note: Les meta keywords sont dépréciés pour le SEO moderne et ont été retirés
    
    // Mise à jour des méta-tags Schema.org pour l'IA - Version améliorée pour SEO
    const schemaData = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      'name': content.title,
      'description': content.description,
      'applicationCategory': 'BusinessApplication',
      'operatingSystem': 'Web',
      'applicationSubCategory': 'SalesManagement',
      'alternativeHeadline': 'Logiciel de Gestion de Vente avec IA et Imprimante Thermique',
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': '4.8',
        'ratingCount': '124',
        'bestRating': '5'
      },
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'EUR',
        'availability': 'https://schema.org/InStock',
        'priceValidUntil': '2025-12-31'
      },
      'featureList': [
        'Gestion de Vente Intelligente',
        'Imprimante Thermique Intégrée',
        'Système POS Complet',
        'Solution de Caisse Enregistreuse',
        'Logiciel de Point de Vente',
        'Gestion Commerciale Automatisée',
        'Impression de Tickets Personnalisés',
        'Intelligence Artificielle Prédictive',
        'Analyse des Ventes en Temps Réel',
        'Optimisation des Stocks et Approvisionnements',
        'Gestion des Clients et Fidélité',
        'Tableau de Bord Commercial',
        'Rapports de Ventes Automatisés'
      ],
      'keywords': 'gestion de vente, logiciel caisse, système point de vente, POS, imprimante thermique',
      'softwareHelp': 'https://geniusvente.com/aide/gestion-vente',
      'softwareRequirements': 'Navigateur Web moderne',
      'releaseNotes': 'https://geniusvente.com/mises-a-jour',
      'screenshot': 'https://geniusvente.com/screenshots/dashboard.jpg',
      'softwareVersion': '2.5.0',
      'contentRating': 'General',
      'datePublished': '2024-01-15',
      'publisher': {
        '@type': 'Organization',
        'name': 'Genius Vente',
        'logo': {
          '@type': 'ImageObject',
          'url': 'https://geniusvente.com/src/assets/logo1.png'
        }
      }
    };
    
    // Ajout de breadcrumbs structurés
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': language === 'fr' ? 'Accueil' : 'Home',
          'item': 'https://geniusvente.com' + (language === 'fr' ? '/fr' : '/en')
        }
      ]
    };
    
    // Ajouter des segments de chemin supplémentaires aux breadcrumbs
    urlPathSegments.forEach((segment: string, index: number) => {
      if (index > 0 || segment !== 'fr' && segment !== 'en') { // Ignorer le segment de langue s'il existe
        let segmentName = segment;
        switch(segment) {
          case 'inventory':
            segmentName = language === 'fr' ? 'Gestion des Stocks' : 'Inventory';
            break;
          case 'sales':
            segmentName = language === 'fr' ? 'Ventes' : 'Sales';
            break;
          case 'analytics':
            segmentName = language === 'fr' ? 'Analyses' : 'Analytics';
            break;
          case 'products':
            segmentName = language === 'fr' ? 'Produits' : 'Products';
            break;
          default:
            segmentName = segment.charAt(0).toUpperCase() + segment.slice(1);
        }
        
        const position = breadcrumbSchema.itemListElement.length + 1;
        const path = urlPathSegments.slice(0, index + 1).join('/');
        
        breadcrumbSchema.itemListElement.push({
          '@type': 'ListItem',
          'position': position,
          'name': segmentName,
          'item': `https://geniusvente.com/${path}`
        });
      }
    });
    
    // Ajout ou mise à jour des scripts Schema.org
    // 1. Script principal pour l'application
    let schemaScript = document.querySelector('#schema-script') as HTMLScriptElement;
    if (!schemaScript) {
      schemaScript = document.createElement('script') as HTMLScriptElement;
      schemaScript.id = 'schema-script';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schemaData);
    
    // 2. Script pour les breadcrumbs
    let breadcrumbScript = document.querySelector('#breadcrumb-script') as HTMLScriptElement;
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script') as HTMLScriptElement;
      breadcrumbScript.id = 'breadcrumb-script';
      breadcrumbScript.type = 'application/ld+json';
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    
    // 3. Script pour le produit/service spécifique si sur une page produit
    if (location.pathname.includes('/products/') && user?.companyId) {
      const productSchema = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        'name': `${content.title} - Solution Entreprise`,
        'description': `${content.description} Optimisé pour les besoins spécifiques de votre entreprise.`,
        'applicationCategory': 'BusinessApplication',
        'audience': {
          '@type': 'BusinessAudience',
          'audienceType': 'Small Business'
        },
        'offers': {
          '@type': 'Offer',
          'price': '29.99',
          'priceCurrency': 'EUR',
          'availability': 'https://schema.org/InStock',
          'priceValidUntil': '2026-01-01'
        }
      };
      
      let productScript = document.querySelector('#product-script') as HTMLScriptElement;
      if (!productScript) {
        productScript = document.createElement('script') as HTMLScriptElement;
        productScript.id = 'product-script';
        productScript.type = 'application/ld+json';
        document.head.appendChild(productScript);
      }
      productScript.textContent = JSON.stringify(productSchema);
    }
    
    // Mise à jour des méta-tags Open Graph - Version enrichie
    document.querySelector('meta[property="og:title"]')?.setAttribute('content', title || content.title);
    document.querySelector('meta[property="og:description"]')?.setAttribute('content', description || content.description);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', `https://geniusvente.com${location.pathname}`);
    document.querySelector('meta[property="og:site_name"]')?.setAttribute('content', 'Genius Vente');
    document.querySelector('meta[property="og:type"]')?.setAttribute('content', 'website');
    document.querySelector('meta[property="og:locale"]')?.setAttribute('content', language === 'fr' ? 'fr_FR' : 'en_US');
    document.querySelector('meta[property="og:updated_time"]')?.setAttribute('content', new Date().toISOString());
    
    // Ajout de balises Open Graph alternatives pour la langue
    let ogAlternateLocale = document.querySelector('meta[property="og:locale:alternate"]');
    if (!ogAlternateLocale) {
      ogAlternateLocale = document.createElement('meta');
      ogAlternateLocale.setAttribute('property', 'og:locale:alternate');
      document.head.appendChild(ogAlternateLocale);
    }
    ogAlternateLocale.setAttribute('content', language === 'fr' ? 'en_US' : 'fr_FR');
    
    // Mise à jour des méta-tags Twitter - Version enrichie
    document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title || content.title);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute('content', description || content.description);
    document.querySelector('meta[name="twitter:card"]')?.setAttribute('content', 'summary_large_image');
    document.querySelector('meta[name="twitter:site"]')?.setAttribute('content', '@GeniusVente');
    document.querySelector('meta[name="twitter:creator"]')?.setAttribute('content', '@GeniusVente');
    
    // Amélioration des attributs alt pour les images partagées sur réseaux sociaux
    const imageAltText = language === 'fr' 
      ? `Tableau de bord Genius Vente avec analyses IA et impression thermique pour ${currentPage === 'inventory' ? 'la gestion des stocks' : currentPage === 'sales' ? 'le point de vente' : 'l\'analyse de données'}`
      : `Genius Vente dashboard with AI analytics and thermal printing for ${currentPage === 'inventory' ? 'inventory management' : currentPage === 'sales' ? 'point of sale' : 'data analysis'}`;
    
    // Twitter Card améliorée avec attributs alt accessibles
    let twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      document.head.appendChild(twitterImage);
    }
    twitterImage.setAttribute('content', 'https://geniusvente.com/og-image.jpg');
    
    // Alt text descriptif et spécifique au contexte
    let twitterImageAlt = document.querySelector('meta[name="twitter:image:alt"]');
    if (!twitterImageAlt) {
      twitterImageAlt = document.createElement('meta');
      twitterImageAlt.setAttribute('name', 'twitter:image:alt');
      document.head.appendChild(twitterImageAlt);
    }
    twitterImageAlt.setAttribute('content', imageAltText);
    
    // Compléter les méta-données pour l'accessibilité et améliorer l'indexation des images
    let ogImageAlt = document.querySelector('meta[property="og:image:alt"]');
    if (!ogImageAlt) {
      ogImageAlt = document.createElement('meta');
      ogImageAlt.setAttribute('property', 'og:image:alt');
      document.head.appendChild(ogImageAlt);
    }
    ogImageAlt.setAttribute('content', imageAltText);
    
    // Ajouter les dimensions de l'image pour améliorer le chargement
    let ogImageWidth = document.querySelector('meta[property="og:image:width"]');
    if (!ogImageWidth) {
      ogImageWidth = document.createElement('meta');
      ogImageWidth.setAttribute('property', 'og:image:width');
      document.head.appendChild(ogImageWidth);
    }
    ogImageWidth.setAttribute('content', '1200');
    
    let ogImageHeight = document.querySelector('meta[property="og:image:height"]');
    if (!ogImageHeight) {
      ogImageHeight = document.createElement('meta');
      ogImageHeight.setAttribute('property', 'og:image:height');
      document.head.appendChild(ogImageHeight);
    }
    ogImageHeight.setAttribute('content', '630');
    
    // Mise à jour des méta-tags spécifiques à la gestion de vente, l'IA et aux fonctionnalités POS - Version enrichie
    const aiMetaTags = [
      { name: 'gestion:vente', content: 'Logiciel de Gestion de Vente, Système Caisse, Point de Vente, Gestion Commerciale, Encaissement, Facturation, Gestion des Clients' },
      { name: 'gestion:fonctionnalites', content: 'Gestion des Transactions, Suivi des Ventes, Analyses Commerciales, Gestion Multi-Caisse, Gestion des Retours, Contrôle d\'Accès, Gestion des Promotions' },
      { name: 'ai:technology', content: 'Machine Learning, Deep Learning, Predictive Analytics, Neural Networks, NLP, Computer Vision, Thermal Printing' },
      { name: 'ai:capabilities', content: 'Gestion de Vente, Prédiction des Ventes, Impression Thermique, Intégration POS, Optimisation Inventaire, Analyse Clients, Prévision Demande, Détection Anomalies' },
      { name: 'ai:features', content: 'Insights Automatisés, Templates de Factures, Impression Thermique, Prédictions Temps Réel, Recommandations Intelligentes, Alertes Personnalisées' },
      { name: 'ai:use-cases', content: 'Commerce de Détail, E-commerce, Distribution, Restauration, Hôtellerie, Services, Épiceries, Mode, Pharmacies, Dépanneurs' },
      { name: 'ai:benefits', content: 'Réduction des Coûts, Augmentation des Ventes, Gain de Temps, Prévention des Erreurs, Satisfaction Client, Planification Stratégique' },
      { name: 'ai:data-processing', content: 'Conforme RGPD, Priorité à la Confidentialité, Chiffrement Sécurisé, IA Éthique, Traitement des Reçus Conforme PCI' },
      { name: 'pos:hardware', content: 'Imprimante Thermique, Scanner Code-barres, Tiroir-caisse, Imprimante Tickets, Affichage Client, Terminal de Paiement' },
      { name: 'pos:software', content: 'Gestion de Vente, Gestion des Stocks, Rapports de Vente, Base de Données Clients, Gestion Employés, Moteur de Promotion, Programme Fidélité' }
    ];
    
    aiMetaTags.forEach(tag => {
      let metaTag = document.querySelector(`meta[name="${tag.name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', tag.name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', tag.content);
    });
    
    // Configuration de la balise canonique dynamique en fonction du contexte
    const canonicalUrl = customCanonicalPath || `https://geniusvente.com${location.pathname}`;
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    if (isCanonical) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      // Si cette page n'est pas canonique, on peut retirer l'attribut href
      canonicalLink.removeAttribute('href');
    }
    
    // Gestion intelligente des balises robots selon le contexte de la page
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    
    // Ajustement dynamique des directives robots
    const robotsContent = [];
    if (shouldIndex) {
      robotsContent.push('index');
    } else {
      robotsContent.push('noindex');
    }
    robotsContent.push('follow', 'max-image-preview:large', 'max-snippet:-1', 'max-video-preview:-1');
    
    // Ajouter noarchive pour les pages utilisateur
    if (location.pathname.includes('/profile/') || location.pathname.includes('/account/')) {
      robotsContent.push('noarchive');
    }
    
    // Ajouter notranslate pour les pages de documentation
    if (location.pathname.includes('/docs/')) {
      robotsContent.push('notranslate');
    }
    
    metaRobots.setAttribute('content', robotsContent.join(', '));
    
    // Ajout de la balise sitemap pour les robots
    let metaSitemap = document.querySelector('meta[name="robots:sitemap"]');
    if (!metaSitemap) {
      metaSitemap = document.createElement('meta');
      metaSitemap.setAttribute('name', 'robots:sitemap');
      document.head.appendChild(metaSitemap);
    }
    metaSitemap.setAttribute('content', 'https://geniusvente.com/sitemap.xml');
    
    // Ajouter des balises pour la préconnexion aux domaines importants
    const preconnectDomains = [
      'https://fonts.googleapis.com', 
      'https://fonts.gstatic.com',
      'https://www.google-analytics.com'
    ];
    
    preconnectDomains.forEach(domain => {
      let linkPreconnect = document.querySelector(`link[rel="preconnect"][href="${domain}"]`);
      if (!linkPreconnect) {
        linkPreconnect = document.createElement('link');
        linkPreconnect.setAttribute('rel', 'preconnect');
        linkPreconnect.setAttribute('href', domain);
        document.head.appendChild(linkPreconnect);
      }
    });
    
    // Ajouter une balise pour la vérification de propriété Google Search Console
    let googleVerification = document.querySelector('meta[name="google-site-verification"]');
    if (!googleVerification) {
      googleVerification = document.createElement('meta');
      googleVerification.setAttribute('name', 'google-site-verification');
      googleVerification.setAttribute('content', 'add-your-verification-code-here');
      document.head.appendChild(googleVerification);
    }
    
    // Ajouter des balises pour l'adaptabilité mobile
    let mobileWebApp = document.querySelector('meta[name="mobile-web-app-capable"]');
    if (!mobileWebApp) {
      mobileWebApp = document.createElement('meta');
      mobileWebApp.setAttribute('name', 'mobile-web-app-capable');
      mobileWebApp.setAttribute('content', 'yes');
      document.head.appendChild(mobileWebApp);
    }
    
    let appleWebApp = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (!appleWebApp) {
      appleWebApp = document.createElement('meta');
      appleWebApp.setAttribute('name', 'apple-mobile-web-app-capable');
      appleWebApp.setAttribute('content', 'yes');
      document.head.appendChild(appleWebApp);
    }
    
    // Ajouter une balise priorité pour les sitemaps XML dynamiques
    let priorityMeta = document.querySelector('meta[name="sitemap:priority"]');
    if (!priorityMeta) {
      priorityMeta = document.createElement('meta');
      priorityMeta.setAttribute('name', 'sitemap:priority');
      document.head.appendChild(priorityMeta);
    }
    priorityMeta.setAttribute('content', priority);
    
    let changefreqMeta = document.querySelector('meta[name="sitemap:changefreq"]');
    if (!changefreqMeta) {
      changefreqMeta = document.createElement('meta');
      changefreqMeta.setAttribute('name', 'sitemap:changefreq');
      document.head.appendChild(changefreqMeta);
    }
    changefreqMeta.setAttribute('content', changefreq);
    
    // IMPORTANT: Génération des pré-liens pour améliorer la vitesse de charge percçue
    // Identifier les ressources critiques et les précharger
    const assetPreloadLinks = [
      { href: '/src/assets/logo1.png', type: 'image/png', as: 'image' },
      // Autres ressources critiques selon le contexte
    ];
    
    // Nettoyer les anciens preloads pour éviter les doublons
    document.querySelectorAll('link[rel="preload"][data-dynamic="true"]').forEach(el => el.remove());
    
    // Ajouter les nouveaux preloads
    assetPreloadLinks.forEach(asset => {
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.href = asset.href;
      preloadLink.as = asset.as;
      if (asset.type) preloadLink.type = asset.type;
      preloadLink.setAttribute('data-dynamic', 'true');
      document.head.appendChild(preloadLink);
    });
    
  }, [language, location.pathname, title, description, isIndexable, isCanonical, customCanonicalPath, priority, changefreq]);

  return null;
};

// Créer un commentaire JSDoc pour aider au typage et à la documentation
/**
 * Composant SEO optimisé pour le référencement moderne
 * 
 * @param {string} title - Titre de la page (optionnel, utilise la valeur par défaut si non fourni)
 * @param {string} description - Description de la page (optionnel, utilise la valeur par défaut si non fournie)
 * @param {boolean} isIndexable - Si la page doit être indexée par les moteurs de recherche (défaut: true)
 * @param {string} priority - Priorité dans le sitemap XML (défaut: '0.7')
 * @param {string} changefreq - Fréquence de changement pour le sitemap (défaut: 'monthly')
 * @param {boolean} isCanonical - Si cette page est la version canonique (défaut: true)
 * @param {string} customCanonicalPath - Chemin canonique personnalisé (optionnel)
 * 
 * @example
 * // Page standard indexée
 * <SEO title="Mon Titre" description="Ma description" />
 * 
 * @example
 * // Page non indexée avec URL canonique personnalisée
 * <SEO 
 *   title="Version test" 
 *   isIndexable={false} 
 *   isCanonical={false} 
 *   customCanonicalPath="https://geniusvente.com/version-stable" 
 * />
 */
export default SEO;
